import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { APP_CONSTANTS } from '../../app.constants';

import { ReportService } from './report.service';

interface FeatureFlag {
    name: string;
    enabled: boolean;
    deprecated: boolean;
    libraries: number[];
}

/**
 * `featureFlags$` uses `null` to represent "not yet loaded".
 * This lets `hasFeature` wait for the HTTP response before emitting,
 * preventing guards from resolving prematurely on page refresh.
 *
 * `initialize()` is called via APP_INITIALIZER so feature flags are
 * fetched before any route activates — the earliest possible moment
 * after authentication context is available.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
    // Shared API URL prefix sourced from APP_CONSTANTS so the "/api" mount
    // point is not hard-coded throughout this service.
    private readonly API_PREFIX = APP_CONSTANTS.API_PREFIX;

    private featureFlags$ = new BehaviorSubject<FeatureFlag[] | null>(null);

    /** Observable that emits `true` once the feature flags HTTP call has resolved. */
    readonly loaded$: Observable<boolean> = this.featureFlags$.pipe(map((flags) => flags !== null));

    constructor(
        private reportService: ReportService,
        private http: HttpClient
    ) {}

    /**
     * Eagerly fetches feature flags from the API and resolves once the
     * response arrives. Registered as an APP_INITIALIZER so the flags
     * are available before any route or component renders.
     */
    initialize(): Promise<void> {
        // Skip the fetch entirely when the app boots without a stored session
        // token (e.g. the login page). Without a token the request would 401
        // and spam the server log with "bad token for /api/feature-flags".
        // AuthService.loginUser() re-invokes loadFeatureFlags() after a
        // successful login, so authenticated flows still get their flags.
        if (!localStorage.getItem('authToken')) {
            this.featureFlags$.next([]);
            return Promise.resolve();
        }

        return firstValueFrom(this.http.get<{ success: boolean; data: FeatureFlag[] }>(`${this.API_PREFIX}/feature-flags`))
            .then(({ data }) => {
                this.featureFlags$.next(data);
            })
            .catch(() => {
                // Resolve gracefully so the app still boots — components that
                // depend on flags will simply not see any enabled features.
                this.featureFlags$.next([]);
            });
    }

    clearFeatureFlags(): void {
        this.featureFlags$.next(null);
    }

    loadFeatureFlags(): void {
        this.http.get<{ success: boolean; data: FeatureFlag[] }>(`${this.API_PREFIX}/feature-flags`).subscribe({
            next: ({ data }) => this.featureFlags$.next(data),
            error: (err) => {
                console.error('Failed to load feature flags:', err);
                // Emit empty array so hasFeature() consumers don't block indefinitely
                this.featureFlags$.next([]);
            }
        });
    }

    hasFeature(flagName: string): Observable<boolean> {
        if (this.featureFlags$.value === null) {
            this.loadFeatureFlags();
        }

        const loaded$ = this.featureFlags$.pipe(filter((flags): flags is FeatureFlag[] => flags !== null));

        // Wait for both flags and a valid currentLibrary before evaluating.
        // LibraryBranchGuard fires-and-forgets library initialization, so
        // monitorChange may still hold null when this runs on page refresh.
        const ready$ = this.reportService.monitorChange.pipe(filter((change) => change.currentLibrary !== null));

        return combineLatest([loaded$, ready$]).pipe(
            map(([flags, { currentLibrary }]) => {
                const flag = flags.find((f) => f.name === flagName);
                if (!flag || !flag.enabled) return false;

                if (flag.deprecated) {
                    console.warn(`Deprecated feature flag: ${flag.name}.`);
                }

                return flag.libraries.length > 0 ? flag.libraries.includes(currentLibrary) : true;
            })
        );
    }
}
